"use client";

import { motion, useScroll, useTransform } from "framer-motion";
import React, { FC, useRef } from "react";

import AnchorLink from "@/components/ui/AnchorLink/AnchorLink";
import Button, { ButtonSize, ButtonTheme, ButtonVariant } from "@/components/ui/Button/Button";
import { buttonClickTracking } from "@/dataLayer/dataLayer";
import useBreakPoint from "@/hooks/useBreakpoint";
import { BaseEntry, CustomLink, Media } from "@/types";

import MiniBrandCardSlider from "./MiniBrandCardSlider";
import "./VerticalCardSlideShow.css";
import WallVerticalCards from "./WallVerticalCards";

export interface CardWidthModal extends BaseEntry {
  image: Media;
  logo: Media;
  modalTitle: string;
  modalDescription?: string;
  modalGallery: Media[];
  modalCTA?: CustomLink;
}

export interface VerticalCardSlideShowProp {
  title: string;
  subtitle: string;
  learnMore?: CustomLink;
  brands: CustomLink[];
  cards?: CardWidthModal[];
}

interface Props {
  entry: VerticalCardSlideShowProp;
}

const VerticalCardSlideShow: FC<Props> = ({ entry }) => {
  const { title, subtitle, learnMore, brands, cards } = entry;
  const { isMd } = useBreakPoint("md");
  const headbar = useRef<any>("headbar");
  const wall = useRef<any>("headbar");
  const container = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: container,
  });
  const headerOpacity = useTransform(scrollYProgress, [0, 0.2], ["1", "0"]);

  return (
    <div ref={container} className={`w-full mx-auto mt-0 relative md:pt-xxl ${cards && "md:min-h-dvh"}`}>
      <motion.div
        style={{ opacity: isMd && cards ? headerOpacity : 1 }}
        ref={headbar}
        className="md:sticky md:top-0 text-center font-primary pt-xxl z-0"
      >
        <div className="px-m md:px-0">
          <h2 className="tracking-wider text-center uppercase font-primary text-m md:text-xl leading-20">{title}</h2>
          <h3 className="text-center text-m font-secondary font-secondary-light pt-l pb-s leading-6">{subtitle}</h3>
        </div>
        {learnMore && (
          <div className="hidden md:pt-l md:block">
            <AnchorLink href={learnMore.link.url}>
              <Button
                size={ButtonSize.MEDIUM}
                theme={ButtonTheme.BLACK}
                type="button"
                variant={ButtonVariant.SECONDARY}
                onClick={() => buttonClickTracking(learnMore.label)}
              >
                {learnMore.label}
              </Button>
            </AnchorLink>
          </div>
        )}
        <MiniBrandCardSlider miniBrands={brands} />
      </motion.div>
      {cards && (
        <div className="wall relative" ref={wall}>
          <WallVerticalCards arrayCards={cards} />
        </div>
      )}
      {learnMore && (
        <div className="block w-full mx-auto text-center md:hidden my-l">
          <AnchorLink href={learnMore.link.url}>
            <Button size={ButtonSize.MEDIUM} theme={ButtonTheme.BLACK} type="button" variant={ButtonVariant.SECONDARY}>
              {learnMore.label}
            </Button>
          </AnchorLink>
        </div>
      )}
    </div>
  );
};
export default VerticalCardSlideShow;
